/**
 * useAuth Hook - Manages authentication state and operations
 * Handles Cognito authentication flow with token management
 * Integrates with SessionManager for user profile synchronization
 */

import { useState, useEffect, useCallback } from 'react';
import { storageService, STORAGE_KEYS } from '../services/storage/StorageService';
import { sessionManager } from '../services/user/SessionManager';
import type { User, AuthTokens, JWTPayload } from '../types/auth';

/**
 * Cognito configuration from environment
 */
const COGNITO_CONFIG = {
  domain: import.meta.env.VITE_COGNITO_DOMAIN || 'us-west-2diyeymilb.auth.us-west-2.amazoncognito.com',
  clientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '6jsljesdvg7748s50lg5ucdi43',
  redirectUri: import.meta.env.VITE_REDIRECT_URI || 'https://www.8bitcomposer.com/',
  logoutUri: import.meta.env.VITE_REDIRECT_URI || 'https://www.8bitcomposer.com/',
};

/**
 * Return type for useAuth hook
 */
export interface UseAuthReturn {
  isAuthenticated: boolean;
  user: User | null;
  idToken: string | null;
  accessToken: string | null;
  isLoading: boolean;
  signIn: () => void;
  signOut: () => void;
  refreshAuth: () => void;
  refreshTokens: () => Promise<void>;
  syncError: Error | null;
}

// ── Helper functions ──────────────────────────────────────────

async function generatePKCE(): Promise<{ verifier: string; challenge: string }> {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const verifier = btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  const challenge = btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  return { verifier, challenge };
}

function parseAuthCodeFromURL(): string | null {
  return new URLSearchParams(window.location.search).get('code');
}

async function exchangeCodeForTokens(code: string, codeVerifier: string): Promise<AuthTokens | null> {
  try {
    const response = await fetch(`https://${COGNITO_CONFIG.domain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: COGNITO_CONFIG.clientId,
        code,
        redirect_uri: COGNITO_CONFIG.redirectUri,
        code_verifier: codeVerifier,
      }).toString(),
    });
    if (!response.ok) {
      console.error('[useAuth] Token exchange failed', await response.text());
      return null;
    }
    const data = await response.json();
    return {
      id_token: data.id_token,
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      token_type: data.token_type,
      expires_in: data.expires_in,
    };
  } catch (error) {
    console.error('[useAuth] Failed to exchange code for tokens', error);
    return null;
  }
}

async function refreshAccessToken(refreshToken: string): Promise<AuthTokens | null> {
  try {
    const response = await fetch(`https://${COGNITO_CONFIG.domain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: COGNITO_CONFIG.clientId,
        refresh_token: refreshToken,
      }).toString(),
    });
    if (!response.ok) {
      console.error('[useAuth] Token refresh failed', await response.text());
      return null;
    }
    const data = await response.json();
    return {
      id_token: data.id_token,
      access_token: data.access_token,
      refresh_token: refreshToken,
      token_type: data.token_type,
      expires_in: data.expires_in,
    };
  } catch (error) {
    console.error('[useAuth] Failed to refresh token', error);
    return null;
  }
}

function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3 || !parts[1]) return null;
    return JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch (error) {
    console.error('[useAuth] Failed to decode JWT', error);
    return null;
  }
}

function extractUser(payload: JWTPayload): User {
  return {
    sub: payload.sub,
    email: payload.email,
    name: payload.name || payload.email,
    ...(payload.given_name && { given_name: payload.given_name }),
    ...(payload.family_name && { family_name: payload.family_name }),
    ...(payload.picture && { picture: payload.picture }),
    email_verified: payload.email_verified,
  };
}

function isTokenExpired(payload: JWTPayload): boolean {
  return payload.exp < Math.floor(Date.now() / 1000);
}


// ── Hook ──────────────────────────────────────────────────────

export function useAuth(): UseAuthReturn {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [syncError, setSyncError] = useState<Error | null>(null);

  /**
   * Single initialization effect that handles BOTH:
   * 1. Authorization code exchange (after Cognito redirect)
   * 2. Loading existing tokens from storage
   *
   * Uses a `cancelled` flag so React StrictMode's unmount/remount cycle
   * doesn't leave us with dead state setters from the first mount.
   */
  useEffect(() => {
    const initialize = async () => {
      try {
        // Step 1: Check if we have an authorization code in the URL
        const authCode = parseAuthCodeFromURL();

        if (authCode) {
          const codeVerifier = storageService.getItem(STORAGE_KEYS.PKCE_VERIFIER);

          // Clear code from URL immediately
          window.history.replaceState(null, '', window.location.pathname);

          if (codeVerifier) {
            const tokens = await exchangeCodeForTokens(authCode, codeVerifier);

            if (tokens) {
              storageService.setItem(STORAGE_KEYS.ID_TOKEN, tokens.id_token);
              storageService.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);
              if (tokens.refresh_token) {
                storageService.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token);
              }
              storageService.removeItem(STORAGE_KEYS.PKCE_VERIFIER);

              const payload = decodeJWT(tokens.id_token);
              if (payload && !isTokenExpired(payload)) {
                const userInfo = extractUser(payload);
                setIsAuthenticated(true);
                setUser(userInfo);
                setIdToken(tokens.id_token);
                setAccessToken(tokens.access_token);
                setIsLoading(false);

                sessionManager.syncUserProfile(tokens.id_token).catch((err) => {
                  console.error('[useAuth] Profile sync failed:', err);
                  setSyncError(err as Error);
                });
                return;
              }
            } else {
              console.error('[useAuth] Code exchange failed');
            }
          } else {
            console.error('[useAuth] No PKCE verifier found');
          }
        }

        // Step 2: check stored tokens
        const storedIdToken = storageService.getItem(STORAGE_KEYS.ID_TOKEN);
        const storedAccessToken = storageService.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        const storedRefreshToken = storageService.getItem(STORAGE_KEYS.REFRESH_TOKEN);

        if (storedIdToken && storedAccessToken) {
          const payload = decodeJWT(storedIdToken);

          if (payload && !isTokenExpired(payload)) {
            const userInfo = extractUser(payload);
            setUser(userInfo);
            setIdToken(storedIdToken);
            setAccessToken(storedAccessToken);
            setIsAuthenticated(true);

            try {
              await sessionManager.initializeSession();
            } catch (syncErr) {
              console.error('[useAuth] Session init failed:', syncErr);
              setSyncError(syncErr as Error);
            }
          } else if (storedRefreshToken) {
            const newTokens = await refreshAccessToken(storedRefreshToken);

            if (newTokens) {
              storageService.setItem(STORAGE_KEYS.ID_TOKEN, newTokens.id_token);
              storageService.setItem(STORAGE_KEYS.ACCESS_TOKEN, newTokens.access_token);
              const newPayload = decodeJWT(newTokens.id_token);
              if (newPayload) {
                setUser(extractUser(newPayload));
                setIdToken(newTokens.id_token);
                setAccessToken(newTokens.access_token);
                setIsAuthenticated(true);
              }
            } else {
              storageService.removeItem(STORAGE_KEYS.ID_TOKEN);
              storageService.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
              storageService.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
              sessionManager.clearSession();
            }
          } else {
            storageService.removeItem(STORAGE_KEYS.ID_TOKEN);
            storageService.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
            sessionManager.clearSession();
          }
        }
      } catch (error) {
        console.error('[useAuth] Initialization failed:', error);
        setSyncError(error as Error);
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
  }, []);

  const signIn = useCallback(async () => {
    const { verifier, challenge } = await generatePKCE();
    storageService.setItem(STORAGE_KEYS.PKCE_VERIFIER, verifier);
    window.location.href = `https://${COGNITO_CONFIG.domain}/oauth2/authorize?` +
      `client_id=${COGNITO_CONFIG.clientId}&` +
      `response_type=code&` +
      `scope=email+openid+profile&` +
      `redirect_uri=${encodeURIComponent(COGNITO_CONFIG.redirectUri)}&` +
      `code_challenge=${challenge}&` +
      `code_challenge_method=S256`;
  }, []);

  const signOut = useCallback(() => {
    sessionManager.clearSession();
    storageService.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    storageService.removeItem(STORAGE_KEYS.PKCE_VERIFIER);
    setIsAuthenticated(false);
    setUser(null);
    setIdToken(null);
    setAccessToken(null);
    setSyncError(null);
    window.location.href = `https://${COGNITO_CONFIG.domain}/logout?` +
      `client_id=${COGNITO_CONFIG.clientId}&` +
      `logout_uri=${encodeURIComponent(COGNITO_CONFIG.logoutUri)}`;
  }, []);

  const refreshAuth = useCallback(() => {
    setIsLoading(true);
    // Re-check stored tokens
    const storedIdToken = storageService.getItem(STORAGE_KEYS.ID_TOKEN);
    const storedAccessToken = storageService.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (storedIdToken && storedAccessToken) {
      const payload = decodeJWT(storedIdToken);
      if (payload && !isTokenExpired(payload)) {
        setUser(extractUser(payload));
        setIdToken(storedIdToken);
        setAccessToken(storedAccessToken);
        setIsAuthenticated(true);
      }
    }
    setIsLoading(false);
  }, []);

  const refreshTokens = useCallback(async () => {
    const refreshToken = storageService.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (!refreshToken) return;
    const newTokens = await refreshAccessToken(refreshToken);
    if (newTokens) {
      storageService.setItem(STORAGE_KEYS.ID_TOKEN, newTokens.id_token);
      storageService.setItem(STORAGE_KEYS.ACCESS_TOKEN, newTokens.access_token);
      const payload = decodeJWT(newTokens.id_token);
      if (payload) {
        setUser(extractUser(payload));
        setIdToken(newTokens.id_token);
        setAccessToken(newTokens.access_token);
        setIsAuthenticated(true);
      }
    } else {
      signOut();
    }
  }, [signOut]);

  return {
    isAuthenticated, user, idToken, accessToken,
    isLoading, signIn, signOut, refreshAuth, refreshTokens, syncError,
  };
}
